/**
 * WorldGalleryScene — dev viewer for the painted-world wave-1 hero renders.
 *
 * AGE-1417: a contact sheet, not a level editor. Loads the manifest at
 * `assets/generated/world/wave1/manifest.json`, lazy-loads each shot's
 * flat painting on demand, and overlays the existing 128×128 Waker idle
 * frame at the manifest-supplied `walkerPos` so the team can verify scale
 * and silhouette read against each painting.
 *
 * Reached via the dev query-string flag `?scene=worldGallery`. BootScene
 * detects the flag and short-circuits straight here, bypassing the title
 * flow. The PNGs are NEVER preloaded — only the currently viewed shot is
 * fetched, and the previous texture is dropped before the next loads.
 */

import * as Phaser from 'phaser';
import { WORLD_GALLERY_SCENE_KEY } from './sceneKeys';

export { WORLD_GALLERY_SCENE_KEY };

/** Painted-world canvas the AI Artist authors against (storyboard §1). */
const PAINTING_DESIGN_W = 1344;
const PAINTING_DESIGN_H = 768;

/** Native Waker frame size — same on-screen scale prod will use. */
const WAKER_NATIVE_SIZE = 128;

const FONT_HEAD = "'Almendra', serif";
const COLOR_INK = '#eae0c8';
const COLOR_INK_DIM = '#9a907a';
const COLOR_BG = 0x14131c;

/** Dev viewer chrome: top status bar + bottom thumbnail rail. */
const TOP_BAR_H = 26;
const BOTTOM_BAR_H = 56;
const FRAME_MARGIN_X = 12;
const FRAME_MARGIN_Y = 6;

const THUMB_W = 40;
const THUMB_H = 23;
const THUMB_GAP = 4;

/** Realm → placeholder palette (from world-bible / storyboard §2). */
const REALM_PALETTE: Record<string, number> = {
  hill: 0x8a9a5b,
  verdana: 0x7c8c4a,
  sablewind: 0xc88a5a,
  frostmere: 0x8aa5c4,
  ember: 0x7a3b2a,
  sky: 0x2a3550,
  all: 0x55504a,
};

interface ManifestShot {
  id: string;
  title: string;
  realm: string;
  composition: string;
  timeOfDay: string;
  flatPath: string | null;
  walkerPos: { x: number; y: number } | null;
  ready: boolean;
  caption?: string;
  cosmologyShot?: boolean;
  cinematicTilt?: boolean;
  heroDeliverable?: boolean;
  frames?: number;
  canvasOverride?: { width: number; height: number };
}

interface Manifest {
  wave: number;
  issue?: string;
  shotBible?: string;
  canvasTarget?: { width: number; height: number };
  shots: ManifestShot[];
}

interface FrameRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface FitResult {
  /** Top-left corner of the painting display rect in canvas coords. */
  x: number;
  y: number;
  /** Display dimensions of the painting in canvas coords. */
  width: number;
  height: number;
  /** Scale factor from painting design space → canvas display space. */
  scale: number;
}

/**
 * Compute the largest letterboxed rect that fits a `designW × designH` source
 * inside the given frame, centered. Pure helper kept exported so a unit test
 * could be added without instantiating Phaser.
 */
export function fitPaintingToFrame(
  frame: FrameRect,
  designW: number,
  designH: number,
): FitResult {
  const scale = Math.min(frame.width / designW, frame.height / designH);
  const width = designW * scale;
  const height = designH * scale;
  const x = frame.x + (frame.width - width) / 2;
  const y = frame.y + (frame.height - height) / 2;
  return { x, y, width, height, scale };
}

/**
 * Map a point from painting design space → canvas display space using the
 * fit result from `fitPaintingToFrame`.
 */
export function paintingToCanvas(
  fit: FitResult,
  point: { x: number; y: number },
): { x: number; y: number } {
  return {
    x: fit.x + point.x * fit.scale,
    y: fit.y + point.y * fit.scale,
  };
}

export class WorldGalleryScene extends Phaser.Scene {
  public static readonly KEY = WORLD_GALLERY_SCENE_KEY;

  private manifest: Manifest | null = null;
  private shots: ManifestShot[] = [];
  private currentIndex = 0;

  /** Texture key currently in memory (so we can drop it on shot change). */
  private loadedTextureKey: string | null = null;

  // Persistent display objects.
  private paintingFrame!: FrameRect;
  private titleText!: Phaser.GameObjects.Text;
  private metaText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private paintingContainer!: Phaser.GameObjects.Container;
  private walker!: Phaser.GameObjects.Image;
  private thumbContainer!: Phaser.GameObjects.Container;
  private thumbHighlights: Phaser.GameObjects.Rectangle[] = [];

  constructor() {
    super({ key: WORLD_GALLERY_SCENE_KEY });
  }

  preload(): void {
    this.load.json(
      'world-wave1-manifest',
      'assets/generated/world/wave1/manifest.json',
    );
    // Reuse the same Waker idle spritesheet TitleScene loads. 128×128 frames.
    this.load.spritesheet(
      'waker-idle',
      'assets/sprites/waker/waker-idle.png',
      { frameWidth: 128, frameHeight: 128 },
    );
  }

  create(): void {
    this.cameras.main.setBackgroundColor(COLOR_BG);

    const raw = this.cache.json.get('world-wave1-manifest') as Manifest | undefined;
    if (!raw || !Array.isArray(raw.shots) || raw.shots.length === 0) {
      this.showFatal('manifest missing or empty');
      return;
    }
    this.manifest = raw;
    this.shots = raw.shots;

    // Painting frame = canvas minus top status bar minus bottom thumb rail.
    this.paintingFrame = {
      x: FRAME_MARGIN_X,
      y: TOP_BAR_H + FRAME_MARGIN_Y,
      width: this.scale.width - FRAME_MARGIN_X * 2,
      height: this.scale.height - TOP_BAR_H - BOTTOM_BAR_H - FRAME_MARGIN_Y * 2,
    };

    this.buildChrome();
    this.buildPaintingLayer();
    this.buildThumbnailRail();
    this.bindNavigation();

    this.showShot(0);
  }

  // ── UI scaffolding ─────────────────────────────────────────────────────────

  private buildChrome(): void {
    // Top bar: shot title + meta.
    this.add
      .rectangle(0, 0, this.scale.width, TOP_BAR_H, 0x000000, 0.55)
      .setOrigin(0, 0);

    this.titleText = this.add
      .text(8, 4, '', {
        fontFamily: FONT_HEAD,
        fontSize: '14px',
        color: COLOR_INK,
      })
      .setOrigin(0, 0);

    this.metaText = this.add
      .text(this.scale.width - 8, 4, '', {
        fontFamily: FONT_HEAD,
        fontSize: '12px',
        color: COLOR_INK_DIM,
      })
      .setOrigin(1, 0);

    // Bottom hint.
    this.hintText = this.add
      .text(
        this.scale.width - 8,
        this.scale.height - 14,
        '← / →   prev/next       click thumbnail to jump',
        {
          fontFamily: FONT_HEAD,
          fontSize: '10px',
          color: COLOR_INK_DIM,
        },
      )
      .setOrigin(1, 1);
  }

  private buildPaintingLayer(): void {
    this.paintingContainer = this.add.container(0, 0);

    // Walker is created up front; its texture exists from preload.
    this.walker = this.add
      .image(0, 0, 'waker-idle', 0)
      .setOrigin(0.5, 1)
      .setVisible(false);
    this.paintingContainer.add(this.walker);
  }

  private buildThumbnailRail(): void {
    const railY = this.scale.height - BOTTOM_BAR_H + 6;
    const totalW = this.shots.length * THUMB_W + (this.shots.length - 1) * THUMB_GAP;
    const startX = (this.scale.width - totalW) / 2;
    this.thumbContainer = this.add.container(0, 0);

    this.shots.forEach((shot, idx) => {
      const x = startX + idx * (THUMB_W + THUMB_GAP);
      const fill = REALM_PALETTE[shot.realm] ?? 0x444444;
      const rect = this.add.rectangle(x, railY, THUMB_W, THUMB_H, fill, shot.ready ? 1.0 : 0.4)
        .setOrigin(0, 0)
        .setStrokeStyle(1, 0x000000, 0.5)
        .setInteractive({ useHandCursor: true });
      rect.on(Phaser.Input.Events.POINTER_DOWN, () => this.showShot(idx));

      const label = this.add.text(x + 2, railY + 1, shot.id.replace('S-', ''), {
        fontFamily: FONT_HEAD,
        fontSize: '9px',
        color: shot.ready ? '#ffffff' : '#cccccc',
      });

      const highlight = this.add
        .rectangle(x - 1, railY - 1, THUMB_W + 2, THUMB_H + 2)
        .setOrigin(0, 0)
        .setStrokeStyle(2, 0xffffff, 1)
        .setVisible(false);
      this.thumbHighlights.push(highlight);

      this.thumbContainer.add([rect, label, highlight]);
    });
  }

  private bindNavigation(): void {
    const kb = this.input.keyboard;
    if (!kb) return;
    kb.on(Phaser.Input.Keyboard.Events.ANY_KEY_DOWN, (event: KeyboardEvent) => {
      switch (event.code) {
        case 'ArrowLeft':
        case 'KeyA':
          this.showShot(this.currentIndex - 1);
          break;
        case 'ArrowRight':
        case 'KeyD':
        case 'Space':
          this.showShot(this.currentIndex + 1);
          break;
      }
    });

    // Click left/right halves of the painting area to navigate too.
    const halfX = this.scale.width / 2;
    const navHit = this.add
      .zone(0, this.paintingFrame.y, this.scale.width, this.paintingFrame.height)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    navHit.on(Phaser.Input.Events.POINTER_DOWN, (p: Phaser.Input.Pointer) => {
      if (p.x < halfX) this.showShot(this.currentIndex - 1);
      else this.showShot(this.currentIndex + 1);
    });
    // Push nav zone behind painting/walker so pointer events on walker still fire.
    this.children.sendToBack(navHit);
  }

  // ── Shot lifecycle ─────────────────────────────────────────────────────────

  private showShot(index: number): void {
    if (this.shots.length === 0) return;
    const wrapped = ((index % this.shots.length) + this.shots.length) % this.shots.length;
    this.currentIndex = wrapped;
    const shot = this.shots[wrapped];

    this.updateChromeFor(shot, wrapped);
    this.updateHighlight(wrapped);
    this.renderShot(shot);
  }

  private updateChromeFor(shot: ManifestShot, index: number): void {
    const counter = `${String(index + 1).padStart(2, '0')}/${String(this.shots.length).padStart(2, '0')}`;
    this.titleText.setText(`${counter}  ${shot.id} — ${shot.title}`);
    const flags: string[] = [shot.realm, shot.timeOfDay];
    if (shot.cosmologyShot) flags.push('cosmology');
    if (shot.cinematicTilt) flags.push('cinematic-tilt');
    if (shot.heroDeliverable) flags.push('hero');
    if (shot.frames && shot.frames > 1) flags.push(`${shot.frames}-frame`);
    if (!shot.ready) flags.push('not-yet-rendered');
    this.metaText.setText(flags.join('  ·  '));
  }

  private updateHighlight(index: number): void {
    this.thumbHighlights.forEach((h, i) => h.setVisible(i === index));
  }

  /**
   * Render the painting + walker for the given shot. Drops the previously
   * loaded full-resolution texture before the next one begins loading so we
   * never hold more than one giant PNG in memory at a time.
   */
  private renderShot(shot: ManifestShot): void {
    // Tear down previous painting display object (walker stays).
    this.paintingContainer.list
      .filter((o) => o !== this.walker)
      .forEach((o) => o.destroy());

    if (this.loadedTextureKey && this.textures.exists(this.loadedTextureKey)) {
      this.textures.remove(this.loadedTextureKey);
      this.loadedTextureKey = null;
    }

    const designW = shot.canvasOverride?.width ?? PAINTING_DESIGN_W;
    const designH = shot.canvasOverride?.height ?? PAINTING_DESIGN_H;
    const fit = fitPaintingToFrame(this.paintingFrame, designW, designH);

    if (shot.ready && shot.flatPath) {
      this.loadFlatPainting(shot, fit);
    } else {
      this.drawPlaceholderCard(shot, fit, designW, designH);
      this.positionWalker(shot, fit, designW, designH);
    }
  }

  private loadFlatPainting(shot: ManifestShot, fit: FitResult): void {
    const key = `world-flat-${shot.id}`;
    // If somehow already cached, reuse synchronously.
    if (this.textures.exists(key)) {
      this.placePainting(key, shot, fit);
      return;
    }

    // Loading-state placeholder while the PNG arrives.
    const loadingRect = this.add
      .rectangle(fit.x, fit.y, fit.width, fit.height, 0x222230)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x55504a);
    const loadingText = this.add
      .text(fit.x + fit.width / 2, fit.y + fit.height / 2, 'loading…', {
        fontFamily: FONT_HEAD,
        fontSize: '14px',
        color: COLOR_INK_DIM,
      })
      .setOrigin(0.5, 0.5);
    this.paintingContainer.add([loadingRect, loadingText]);

    this.load.image(key, shot.flatPath as string);
    // Phaser dispatches a per-file event named `filecomplete-<type>-<key>`.
    const onComplete = (): void => {
      this.load.off(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
      // Bail if user has navigated away during load.
      if (this.shots[this.currentIndex]?.id !== shot.id) {
        if (this.textures.exists(key)) this.textures.remove(key);
        return;
      }
      loadingRect.destroy();
      loadingText.destroy();
      this.placePainting(key, shot, fit);
    };
    const onError = (): void => {
      this.load.off(`filecomplete-image-${key}`, onComplete);
      // Treat a failed load like "not yet rendered" so the gallery stays usable.
      if (this.shots[this.currentIndex]?.id !== shot.id) return;
      loadingRect.destroy();
      loadingText.destroy();
      this.drawPlaceholderCard(
        shot,
        fit,
        shot.canvasOverride?.width ?? PAINTING_DESIGN_W,
        shot.canvasOverride?.height ?? PAINTING_DESIGN_H,
        '(failed to load — placeholder shown)',
      );
      this.positionWalker(
        shot,
        fit,
        shot.canvasOverride?.width ?? PAINTING_DESIGN_W,
        shot.canvasOverride?.height ?? PAINTING_DESIGN_H,
      );
    };
    this.load.once(`filecomplete-image-${key}`, onComplete);
    this.load.once(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
    this.load.start();
  }

  private placePainting(key: string, shot: ManifestShot, fit: FitResult): void {
    this.loadedTextureKey = key;
    const img = this.add.image(fit.x, fit.y, key).setOrigin(0, 0);
    img.setDisplaySize(fit.width, fit.height);
    this.paintingContainer.add(img);
    this.paintingContainer.bringToTop(this.walker);
    const designW = shot.canvasOverride?.width ?? PAINTING_DESIGN_W;
    const designH = shot.canvasOverride?.height ?? PAINTING_DESIGN_H;
    this.positionWalker(shot, fit, designW, designH);
  }

  private drawPlaceholderCard(
    shot: ManifestShot,
    fit: FitResult,
    designW: number,
    designH: number,
    note?: string,
  ): void {
    const palette = REALM_PALETTE[shot.realm] ?? 0x55504a;

    const bg = this.add
      .rectangle(fit.x, fit.y, fit.width, fit.height, palette, 0.65)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0x000000, 0.4);
    this.paintingContainer.add(bg);

    // Frame indicator showing the painting design size.
    const sizeText = this.add
      .text(fit.x + 8, fit.y + 8, `${designW} × ${designH}`, {
        fontFamily: FONT_HEAD,
        fontSize: '10px',
        color: COLOR_INK_DIM,
      })
      .setOrigin(0, 0);
    this.paintingContainer.add(sizeText);

    // Title sits in the upper third; subtitle in the lower third, so the
    // Waker sprite (typically positioned around frame center / bottom-third)
    // doesn't collide with the placeholder labelling.
    const headline = this.add
      .text(
        fit.x + fit.width / 2,
        fit.y + fit.height * 0.28,
        `${shot.id}\n${shot.title}`,
        {
          fontFamily: FONT_HEAD,
          fontSize: '20px',
          color: COLOR_INK,
          align: 'center',
          wordWrap: { width: fit.width - 32 },
        },
      )
      .setOrigin(0.5, 0.5);
    this.paintingContainer.add(headline);

    const subtitle = this.add
      .text(
        fit.x + fit.width / 2,
        fit.y + fit.height - 18,
        note ?? `${shot.realm} · ${shot.composition} · ${shot.timeOfDay}`,
        {
          fontFamily: FONT_HEAD,
          fontSize: '12px',
          color: COLOR_INK_DIM,
          align: 'center',
          wordWrap: { width: fit.width - 32 },
        },
      )
      .setOrigin(0.5, 1);
    this.paintingContainer.add(subtitle);
  }

  private positionWalker(
    shot: ManifestShot,
    fit: FitResult,
    designW: number,
    designH: number,
  ): void {
    if (shot.walkerPos === null) {
      this.walker.setVisible(false);
      return;
    }
    // Default to bottom-center of the painting in design space.
    const designPos =
      shot.walkerPos ?? { x: designW / 2, y: designH - WAKER_NATIVE_SIZE / 4 };
    const { x, y } = paintingToCanvas(fit, designPos);
    this.walker.setPosition(x, y);
    // Walker scales with painting so it reads at the same in-frame size the
    // prod game will render it (walker is 128px in painting design space too).
    this.walker.setDisplaySize(WAKER_NATIVE_SIZE * fit.scale, WAKER_NATIVE_SIZE * fit.scale);
    this.walker.setVisible(true);
    this.paintingContainer.bringToTop(this.walker);
  }

  // ── Errors ─────────────────────────────────────────────────────────────────

  private showFatal(message: string): void {
    this.add
      .text(this.scale.width / 2, this.scale.height / 2, `world-gallery: ${message}`, {
        fontFamily: FONT_HEAD,
        fontSize: '16px',
        color: '#ff8888',
      })
      .setOrigin(0.5, 0.5);
  }
}
